import { BillableMaterial, CreateBillableMaterial } from './types';
import { useBillableMaterials } from '../../api/queries/billableMaterials';
import { useCreateBillableMaterial } from '../../api/mutations/billableMaterials/useCreateBillableMaterial';
import { useUpdateBillableMaterial } from '../../api/mutations/billableMaterials/useUpdateBillableMaterial';
import { useDeleteBillableMaterial } from '../../api/mutations/billableMaterials/useDeleteBillableMaterial';
import { TEMP_ID_PREFIX } from './constants';
import { useMemo, useState } from 'react';
import _ from 'lodash';
import i18n from '../../i18n';

export function useBillableMaterialsController(organisationId: string) {
  const { data: materials } = useBillableMaterials({ organisationId });
  const { mutateAsync: createBillableMaterial } = useCreateBillableMaterial();
  const { mutateAsync: updateBillableMaterial } = useUpdateBillableMaterial();
  const { mutateAsync: deleteBillableMaterial } = useDeleteBillableMaterial();

  const [activeMaterial, setActiveMaterial] = useState<BillableMaterial | undefined>();

  const dirtyMaterialId = useMemo(() => {
    if (!activeMaterial) return undefined;
    const original = materials?.find((m) => m.id === activeMaterial.id);

    if (!original) return activeMaterial.id;

    return _.isEqual(activeMaterial, original) ? undefined : activeMaterial.id;
  }, [materials, activeMaterial]);

  const displayMaterials = useMemo(() => {
    let list = [...(materials ?? [])];
    if (!activeMaterial) return list;

    const index = list.findIndex((m) => m.id === activeMaterial.id);

    if (index !== -1) list[index] = activeMaterial;
    else if (activeMaterial.id.startsWith(TEMP_ID_PREFIX)) {
      list.push(activeMaterial);
    }
    return list;
  }, [materials, activeMaterial]);

  const deleteMaterial = async (billableMaterialId: string) => {
    if (!billableMaterialId.startsWith(TEMP_ID_PREFIX)) {
      await deleteBillableMaterial({ organisationId, billableMaterialId });
    } else {
      setActiveMaterial(undefined);
    }
  };

  const saveMaterialIfDirty = async () => {
    if (!activeMaterial) return;

    const original = materials?.find((m) => m.id === activeMaterial.id);
    const isDirty = !original || !_.isEqual(activeMaterial, original);

    if (!isDirty) return original;

    await saveMaterial(activeMaterial);
  };

  const saveMaterial = async (bm: BillableMaterial) => {
    let result;
    if (bm.id.startsWith(TEMP_ID_PREFIX)) {
      const { ...data } = bm;
      if (data.name === '') data.name = i18n.t('billableMaterial.new');
      result = await createBillableMaterial({ organisationId, data: data as CreateBillableMaterial });
      setActiveMaterial(result);
    } else {
      result = await updateBillableMaterial({ organisationId, billableMaterialId: bm.id, billableMaterial: bm });
    }
    setActiveMaterial(result);
  };

  const addNewMaterial = async (bm?: BillableMaterial) => {
    await saveMaterialIfDirty();

    const isCopy = !!bm?.id;

    const newMaterial = {
      id: `${TEMP_ID_PREFIX}${new Date().toISOString()}`,
      name: isCopy ? `${bm.name} ${i18n.t('billableMaterial.copy')}` : i18n.t('billableMaterial.new'),
      conditionSets: isCopy ? bm.conditionSets : [{ id: 'rule-1', conditions: [] }],
      jobsiteIds: isCopy ? bm.jobsiteIds : [],
    };

    setActiveMaterial(newMaterial);
  };

  const selectMaterial = async (bm: BillableMaterial | undefined) => {
    if (activeMaterial?.id === bm?.id) return;

    await saveMaterialIfDirty();
    setActiveMaterial(bm);
  };

  const updateActiveItem = (update: BillableMaterial) => {
    setActiveMaterial(update);
  };

  return {
    materials,
    deleteMaterial,
    saveMaterialIfDirty,
    selectMaterial,
    addNewMaterial,
    activeMaterial,
    updateActiveItem,
    dirtyMaterialId,
    displayMaterials,
  };
}
